<script setup lang="ts">
/**
 * 画像編集画面(画像編集の作り直し)。
 *
 * 3つの行で編集し、「登録する」を押したときに初めて商品画像を変更する。
 *   1行目 現在の画像   : 登録済み(amazon.image の Large)。この行は変わらない
 *   2行目 候補         : Amazon / AI / ローカル の区分で横一列。一時置き場にある未登録の画像
 *   3行目 登録する画像 : この並びで登録される(左から ImageNo = 0, 1, 2…)
 * 1・2行目からは「追加」ボタンかドラッグで3行目へ入れる。3行目に入っている画像は1・2行目でグレー表示。
 * 3行目はドラッグで並べ替え、「除外」で外す。キャンセルすると何も変わらない。
 *
 * 【17番画像(v13)】PSE画像等、必ずモールへ表示する画像。候補の「17番画像」で指定した1枚だけが
 * ImageNo = 17 になる(並び替えに関係なく常に17)。3行目では右端に分けて表示する。
 * 通常の画像は 0 から振り、15, 16, 17, 18, 19, 100 は飛ばす(サーバー側 saveProductImages と同じ決まり)。
 *
 * 以前の「Amazonから画像更新」「画像ファイルを追加」「削除」「前へ/後へ」は押した瞬間に
 * DBとS3を書き換えていたため、この画面にまとめて置き換えた。
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useProductImages, useSaveProductImagesMutation } from '@/api/queries'
import * as api from '@/api/endpoints'
import { ApiError } from '@/api/client'
import type { AiImageEditJob, AiImageEditResult, ProductImage, ProductImageOcr, SaveImageItem } from '@/types/api'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useSelectionStore } from '@/stores/selection'
import { useRelatedFileCounts } from '@/api/queries'
import { runRelatedFileBat } from '@/utils/localExec'
import { useResultModalStore } from '@/stores/resultModal'
import { useToastStore } from '@/stores/toast'
import OcrImage from '../OcrImage.vue'
import OcrNgList from '../OcrNgList.vue'
import AiEditModal from './AiEditModal.vue'
import PaintEditModal from './PaintEditModal.vue'
import { ocrHits } from '@/utils/ocrNg'
import {
  ocrChips,
  newId,
  ACCEPT_IMAGE_TYPES,
  type Candidate,
  type CandidateOrigin,
  type AiSource,
} from '@/utils/imageEditor'

const props = withDefaults(
  defineProps<{
    asin: string
    regionCode?: string
    parentAsin?: string | null
  }>(),
  { regionCode: 'jp', parentAsin: null },
)
const emit = defineEmits<{ close: [] }>()

const LARGE = 5
/** 17番画像(PSE画像等、必ずモールへ表示する画像)の ImageNo */
const PSE_NO = 17
/** 通常の画像に使わない ImageNo */
const RESERVED_NOS = new Set([15, 16, 17, 18, 19, 100])
/** 通常の画像 count 枚に振る ImageNo(0から、予約番号を飛ばす) */
function regularNos(count: number): number[] {
  const nos: number[] = []
  for (let n = 0; nos.length < count; n++) if (!RESERVED_NOS.has(n)) nos.push(n)
  return nos
}
const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()
const toast = useToastStore()

// ---------------------------------------------------------------------------
// 1行目: 現在の画像(画面を開いた時点のもの。登録時の版もこの時点で固定する)
// ---------------------------------------------------------------------------
const asinRef = computed(() => props.asin)
const { data: imagesData, refetch } = useProductImages(asinRef)

const ready = ref(false)
const loadError = ref<string | null>(null)
const baseVersion = ref<string | null>(null)
const snapshot = ref<ProductImage[]>([])

function stripQuery(url: string) {
  return url.split('?')[0]
}

/** 開いた時点の一覧に、その後の再取得で届いたOCR結果だけを重ねる */
const currentImages = computed<ProductImage[]>(() =>
  snapshot.value.map((img) => {
    const live = imagesData.value?.images.find(
      (l) => l.ImageTypeId === LARGE && l.ImageNo === img.ImageNo && stripQuery(l.ImageUrl) === stripQuery(img.ImageUrl),
    )
    return live ? { ...img, ocr: live.ocr } : img
  }),
)
const currentByNo = computed(() => new Map(currentImages.value.map((i) => [i.ImageNo, i])))

async function init() {
  try {
    const res = await refetch()
    const data = res.data
    if (!data) throw res.error ?? new Error('画像の取得に失敗しました。')
    snapshot.value = data.images.filter((i) => i.ImageTypeId === LARGE).sort((a, b) => a.ImageNo - b.ImageNo)
    baseVersion.value = data.version ?? null
    resetToCurrent()
    ready.value = true
    loadAiHistory()
  } catch (e) {
    loadError.value = `画像の取得に失敗しました: ${(e as Error).message}`
  }
}

/** 日時を「10/11 17:20」の形にする */
function shortDate(v: string | Date) {
  const d = typeof v === 'string' ? new Date(v.replace(' ', 'T')) : v
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function aiCandidate(r: AiImageEditResult, jobId: string, createdAt: string | Date, sourceText: string, prompt: string): Candidate {
  return {
    id: newId('ai'),
    origin: 'ai',
    stagingKey: r.stagingKey,
    url: r.url,
    label: shortDate(createdAt),
    sub: `${sourceText}を修正`,
    ocr: r.ocr,
    aiJobId: jobId,
    aiIndex: r.index,
    title: prompt,
  }
}

/**
 * この商品で過去に作ったAI修正の結果を、候補(AI)に読み込む(新しい順)。
 * AIの結果は登録しなくてもS3に残しているので、画面を開き直しても見直して使える。
 */
const aiHistoryLoading = ref(false)
async function loadAiHistory() {
  aiHistoryLoading.value = true
  try {
    const res = await api.listAiImageEdits(props.asin, props.regionCode)
    const known = new Set(candidates.value.map((c) => c.stagingKey))
    const past: Candidate[] = []
    for (const j of res.jobs) {
      const sourceText = j.sourceImageNo !== null && j.sourceImageNo !== undefined ? `#${j.sourceImageNo}` : '候補'
      for (const r of j.results) {
        if (known.has(r.stagingKey)) continue
        known.add(r.stagingKey)
        past.push(aiCandidate(r, j.jobId, j.createDate, sourceText, j.prompt))
      }
    }
    // この画面で作った結果(先頭)の後ろに、過去の結果を並べる
    const ai = candidates.value.filter((c) => c.origin === 'ai')
    const others = candidates.value.filter((c) => c.origin !== 'ai')
    candidates.value = [...others, ...ai, ...past]
  } catch (e) {
    console.warn('過去のAI修正の結果を読み込めませんでした', e)
  } finally {
    aiHistoryLoading.value = false
  }
}

// ---------------------------------------------------------------------------
// 2行目: 候補
// ---------------------------------------------------------------------------
const candidates = ref<Candidate[]>([])
const candById = computed(() => new Map(candidates.value.map((c) => [c.id, c])))
const GROUPS: { origin: CandidateOrigin; label: string }[] = [
  { origin: 'amazon', label: 'Amazon' },
  { origin: 'ai', label: 'AI' },
  { origin: 'local', label: 'ローカル' },
  { origin: 'pse', label: '17番画像' },
]
const groupedCandidates = computed(() =>
  GROUPS.map((g) => ({ ...g, items: candidates.value.filter((c) => c.origin === g.origin) })),
)

// 候補(Amazon・ローカル)はその場でOCRしない。登録して amazon.image に入れば、
// OCRワーカーがバックグラウンドで処理する。AIの結果だけは修正の確認のためワーカー側でOCR済み。

// ---- Amazon ----
const amazonState = ref<'idle' | 'loading' | 'done' | 'error'>('idle')
const amazonError = ref<string | null>(null)
const amazonFetchedAt = ref<string | null>(null)

async function fetchAmazon() {
  amazonState.value = 'loading'
  amazonError.value = null
  try {
    const res = await api.getAmazonImageCandidates(props.asin, props.regionCode)
    // 3行目に入っていない古いAmazon候補は入れ替える
    const keep = candidates.value.filter((c) => c.origin !== 'amazon' || candInDraft.value.has(c.id))
    const added: Candidate[] = res.candidates.map((a) => ({
      id: newId('amz'),
      origin: 'amazon',
      stagingKey: a.stagingKey,
      url: a.url,
      label: a.variant,
      sub: '',
      match: a.match,
    }))
    candidates.value = [...keep, ...added]
    const d = new Date()
    amazonFetchedAt.value = `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    amazonState.value = 'done'
  } catch (e) {
    amazonError.value = (e as Error).message
    amazonState.value = 'error'
  }
}

// ---- ローカル ----
const fileInput = ref<HTMLInputElement | null>(null)
const uploadingCount = ref(0)
const objectUrls: string[] = []

function pickFiles() {
  fileInput.value?.click()
}
function onFilesPicked(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files?.length) addLocalFiles(Array.from(input.files))
  input.value = ''
}

/** ローカルの画像を一時置き場へ上げて候補にする。draftAt を渡すと3行目のその位置にも入れる */
async function addLocalFiles(files: File[], draftAt?: number) {
  const images = files.filter((f) => ACCEPT_IMAGE_TYPES.includes(f.type))
  if (images.length < files.length) toast.show('JPEG / PNG / WebP / GIF 以外のファイルは追加できません。', 'ng')
  let at = draftAt
  for (const f of images) {
    const c = await uploadLocal(f)
    if (c && at !== undefined) {
      insertDraft({ kind: 'cand', candId: c.id }, at)
      at++
    }
  }
}

/** 1枚を一時置き場へ上げて候補(ローカル)にする。失敗したら null */
async function uploadLocal(f: File, sub = '', origin: CandidateOrigin = 'local'): Promise<Candidate | null> {
  if (!ACCEPT_IMAGE_TYPES.includes(f.type)) {
    toast.show('JPEG / PNG / WebP / GIF 以外のファイルは追加できません。', 'ng')
    return null
  }
  uploadingCount.value++
  try {
    const { stagingKey } = await api.uploadToStaging(f, f.type)
    const url = URL.createObjectURL(f)
    objectUrls.push(url)
    const c: Candidate = { id: newId(origin === 'pse' ? 'pse' : 'loc'), origin, stagingKey, url, label: f.name, sub }
    candidates.value = [...candidates.value, c]
    return c
  } catch (e) {
    resultModal.show(`${f.name} のアップロードに失敗しました: ${(e as Error).message}`, 'ng')
    return null
  } finally {
    uploadingCount.value--
  }
}

// ---------------------------------------------------------------------------
// 3行目: 登録する画像
// ---------------------------------------------------------------------------
type DraftRef = { kind: 'current'; imageNo: number } | { kind: 'cand'; candId: string }
interface DraftItem {
  key: string
  ref: DraftRef
}
const draft = ref<DraftItem[]>([])
/** 17番画像(無ければ null)。3行目の並びとは別に持つ */
const pse = ref<DraftItem | null>(null)
/** 3行目の並び + 17番画像 */
const allSelected = computed(() => (pse.value ? [...draft.value, pse.value] : draft.value))

const currentInDraft = computed(
  () => new Set(allSelected.value.flatMap((d) => (d.ref.kind === 'current' ? [d.ref.imageNo] : []))),
)
const candInDraft = computed(() => new Set(allSelected.value.flatMap((d) => (d.ref.kind === 'cand' ? [d.ref.candId] : []))))

/** 候補が3行目に入っているか。現在の画像と同一のAmazon候補は、その現在の画像が入っていれば入っている扱い */
function isCandUsed(c: Candidate) {
  if (candInDraft.value.has(c.id)) return true
  return c.match?.kind === 'same' && currentInDraft.value.has(c.match.imageNo)
}

interface DraftView {
  key: string
  url: string
  ocr?: ProductImageOcr
  checking: boolean
  kind: 'current' | CandidateOrigin
  srcText: string
  isNew: boolean
}
function toView(d: DraftItem): DraftView {
    if (d.ref.kind === 'current') {
      const img = currentByNo.value.get(d.ref.imageNo)
      return {
        key: d.key,
        url: img?.ImageUrl ?? '',
        ocr: img?.ocr,
        checking: false,
        kind: 'current',
        srcText: `現在 #${d.ref.imageNo}`,
        isNew: false,
      }
    }
    const c = candById.value.get(d.ref.candId)
    return {
      key: d.key,
      url: c?.url ?? '',
      ocr: c?.ocr,
      checking: !!c?.ocrChecking,
      kind: c?.origin ?? 'local',
      srcText: c ? candSourceText(c) : '',
      isNew: true,
    }
}
const draftView = computed<DraftView[]>(() => draft.value.map(toView))
const pseView = computed<DraftView | null>(() => (pse.value ? toView(pse.value) : null))

/** 3行目のOCRの印。まだOCRしていない新しい画像は「登録後にOCR」 */
function draftChips(d: DraftView) {
  if (d.ocr) return ocrChips(d.ocr, d.checking)
  return d.isNew ? [{ text: '登録後OCR', cls: 'ocr-badge ocr-badge-pending', title: '登録すると、バックグラウンドでOCRされます' }] : []
}

function candSourceText(c: Candidate) {
  if (c.origin === 'amazon') return `Amazon ${c.label}`
  if (c.origin === 'ai') return `AI ${c.label}`
  if (c.id.startsWith('edit')) return '編集'
  if (c.origin === 'pse') return 'ファイル'
  return 'ローカル'
}

function insertDraft(ref: DraftRef, at?: number) {
  const arr = [...draft.value]
  arr.splice(at ?? arr.length, 0, { key: newId('d'), ref })
  draft.value = arr
}
function addCurrent(no: number) {
  if (!currentInDraft.value.has(no)) insertDraft({ kind: 'current', imageNo: no })
}
function addCandidate(c: Candidate) {
  if (isCandUsed(c)) return
  // 「17番画像」の群の画像は、17番画像にする(差し替え前に上げた画像を選び直すとき)
  if (c.origin === 'pse') setPse({ kind: 'cand', candId: c.id })
  else insertDraft({ kind: 'cand', candId: c.id })
}
/** 17番画像にする。今の17番画像は外れる(1行目・候補に戻る) */
function setPse(ref: DraftRef) {
  // 3行目に同じ画像があれば、そこからは外す(同じ画像を2回登録しない)
  draft.value = draft.value.filter((d) => JSON.stringify(d.ref) !== JSON.stringify(ref))
  pse.value = { key: newId('pse'), ref }
}
function removePse() {
  pse.value = null
}
function removeDraft(index: number) {
  const arr = [...draft.value]
  arr.splice(index, 1)
  draft.value = arr
}
/** from の画像を、並びの at の位置(移動前の添字で数えた挿入位置)へ動かす */
function moveDraft(from: number, at: number) {
  const arr = [...draft.value]
  const [item] = arr.splice(from, 1)
  arr.splice(at > from ? at - 1 : at, 0, item)
  draft.value = arr
}
function resetToCurrent() {
  // 今の ImageNo 17 は 17番画像として扱う(そのまま登録し直しても 17 のまま)
  draft.value = currentImages.value
    .filter((img) => img.ImageNo !== PSE_NO)
    .map((img) => ({ key: newId('d'), ref: { kind: 'current', imageNo: img.ImageNo } }))
  pse.value = currentByNo.value.has(PSE_NO) ? { key: newId('pse'), ref: { kind: 'current', imageNo: PSE_NO } } : null
}
/** 3行目をAmazonの画像の並びにする。現在の画像と同一のものは現在の画像を使う(OCR結果などを引き継ぐ) */
function replaceWithAmazon() {
  const amazon = candidates.value.filter((c) => c.origin === 'amazon')
  draft.value = amazon.map((c) => ({
    key: newId('d'),
    ref:
      c.match?.kind === 'same' && currentByNo.value.has(c.match.imageNo)
        ? { kind: 'current', imageNo: c.match.imageNo }
        : { kind: 'cand', candId: c.id },
  }))
}

// ---- 変更内容 ----
/** 登録したときに付く ImageNo(3行目の順。17番画像は17) */
const plannedNos = computed(() => regularNos(draft.value.length))
/** 今の画像のうち、番号だけ変わるものがある(15〜19・100 の旧データ、番号の欠け等) */
const needsRenumber = computed(() =>
  draft.value.some((d, i) => d.ref.kind === 'current' && d.ref.imageNo !== plannedNos.value[i]) ||
  (pse.value?.ref.kind === 'current' && pse.value.ref.imageNo !== PSE_NO),
)
const isDirty = computed(() => {
  const planned: [DraftRef, number][] = draft.value.map((d, i) => [d.ref, plannedNos.value[i]])
  if (pse.value) planned.push([pse.value.ref, PSE_NO])
  if (planned.length !== currentImages.value.length) return true
  return planned.some(([r, no]) => r.kind !== 'current' || r.imageNo !== no)
})
const summary = computed(() => {
  const added = allSelected.value.filter((d) => d.ref.kind === 'cand').length
  const removed = currentImages.value.filter((img) => !currentInDraft.value.has(img.ImageNo)).length
  const order = draft.value.flatMap((d) => (d.ref.kind === 'current' ? [d.ref.imageNo] : []))
  const reordered = order.some((n, i) => i > 0 && n < order[i - 1])
  return { added, removed, reordered }
})
const ngInDraft = computed(
  () => [...draftView.value, ...(pseView.value ? [pseView.value] : [])].filter((d) => ocrHits(d.ocr).length > 0).length,
)

// ---------------------------------------------------------------------------
// ドラッグ&ドロップ
// ---------------------------------------------------------------------------
type DragPayload =
  | { type: 'current'; imageNo: number }
  | { type: 'cand'; id: string }
  | { type: 'draft'; index: number }
  | { type: 'pse' }
const dragging = ref<DragPayload | null>(null)
const dropIndex = ref<number | null>(null)

function onDragStart(e: DragEvent, p: DragPayload) {
  dragging.value = p
  if (e.dataTransfer) {
    e.dataTransfer.setData('text/plain', 'salmon-image')
    e.dataTransfer.effectAllowed = p.type === 'draft' ? 'move' : 'copy'
  }
}
function onDragEnd() {
  dragging.value = null
  dropIndex.value = null
}
function onDraftTileDragOver(e: DragEvent, index: number) {
  e.preventDefault()
  e.stopPropagation()
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dropIndex.value = e.clientX < rect.left + rect.width / 2 ? index : index + 1
}
function onDraftAreaDragOver(e: DragEvent) {
  e.preventDefault()
  if (dropIndex.value === null) dropIndex.value = draft.value.length
}
function onDraftAreaDragLeave(e: DragEvent) {
  const to = e.relatedTarget as Node | null
  if (!to || !(e.currentTarget as HTMLElement).contains(to)) dropIndex.value = null
}
function onDraftDrop(e: DragEvent) {
  e.preventDefault()
  const at = dropIndex.value ?? draft.value.length
  const p = dragging.value
  dragging.value = null
  dropIndex.value = null
  if (!p) {
    const files = e.dataTransfer?.files
    if (files?.length) addLocalFiles(Array.from(files), at)
    return
  }
  if (p.type === 'draft') moveDraft(p.index, at)
  else if (p.type === 'pse') {
    // 17番画像を通常の並びへ移す
    if (pse.value) insertDraft(pse.value.ref, at)
    pse.value = null
  } else if (p.type === 'current') {
    if (!currentInDraft.value.has(p.imageNo)) insertDraft({ kind: 'current', imageNo: p.imageNo }, at)
  } else {
    const c = candById.value.get(p.id)
    if (c && !isCandUsed(c)) insertDraft({ kind: 'cand', candId: c.id }, at)
  }
}
/** 候補の「17番画像」へのドロップ: ファイルはアップロードして17番画像に、画面内の画像はそのまま17番画像に */
const pseDropOver = ref(false)
function onPseDrop(e: DragEvent) {
  e.preventDefault()
  e.stopPropagation()
  pseDropOver.value = false
  const p = dragging.value
  dragging.value = null
  dropIndex.value = null
  if (!p) {
    const files = e.dataTransfer?.files
    if (files?.length) addPseFile(files[0])
    return
  }
  if (p.type === 'current') setPse({ kind: 'current', imageNo: p.imageNo })
  else if (p.type === 'cand') setPse({ kind: 'cand', candId: p.id })
  else if (p.type === 'draft') {
    const d = draft.value[p.index]
    if (d) setPse(d.ref)
  }
}
const pseInput = ref<HTMLInputElement | null>(null)
function pickPseFile() {
  pseInput.value?.click()
}
function onPseFilePicked(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files?.length) addPseFile(input.files[0])
  input.value = ''
}
async function addPseFile(f: File) {
  // 17番画像の枠から上げた画像は、候補の「17番画像」の群に出す(ローカルには出さない)
  const c = await uploadLocal(f, '', 'pse')
  if (c) setPse({ kind: 'cand', candId: c.id })
}

/** 候補の行へファイルをドロップしたら、候補(ローカル)に追加するだけ */
function onCandDrop(e: DragEvent) {
  e.preventDefault()
  if (dragging.value) return
  const files = e.dataTransfer?.files
  if (files?.length) addLocalFiles(Array.from(files))
}

// ---------------------------------------------------------------------------
// 拡大表示(OCRの枠・NGワード一覧つき)
// ---------------------------------------------------------------------------
interface LbItem {
  url: string
  ocr?: ProductImageOcr
  label: string
}
const lb = ref<{ items: LbItem[]; index: number } | null>(null)
const lbItem = computed(() => (lb.value ? lb.value.items[lb.value.index] : null))
const lbHits = computed(() => ocrHits(lbItem.value?.ocr))
const lbActiveHit = ref<number | null>(null)
const lbShowBoxes = ref(true)

function openLbCurrent(index: number) {
  lb.value = { items: currentImages.value.map((i) => ({ url: i.ImageUrl, ocr: i.ocr, label: `現在 #${i.ImageNo}` })), index }
}
function openLbCand(c: Candidate) {
  const items = candidates.value.map((x) => ({ url: x.url, ocr: x.ocr, label: candSourceText(x) }))
  lb.value = { items, index: candidates.value.findIndex((x) => x.id === c.id) }
}
function openLbDraft(index: number) {
  const items = draftView.value.map((d, i) => ({ url: d.url, ocr: d.ocr, label: `${i === 0 ? 'メイン画像' : plannedNos.value[i]} · ${d.srcText}` }))
  if (pseView.value) items.push({ url: pseView.value.url, ocr: pseView.value.ocr, label: `17番画像 · ${pseView.value.srcText}` })
  lb.value = { items, index }
}
function lbStep(delta: number) {
  if (!lb.value) return
  const n = lb.value.items.length
  lb.value.index = (lb.value.index + delta + n) % n
  lbActiveHit.value = null
}
function onKeydown(e: KeyboardEvent) {
  if (!lb.value) return
  if (e.key === 'ArrowLeft') lbStep(-1)
  else if (e.key === 'ArrowRight') lbStep(1)
  else if (e.key === 'Escape') {
    e.stopPropagation()
    lb.value = null
  }
}

// ---------------------------------------------------------------------------
// AI修正
// ---------------------------------------------------------------------------
const aiSource = ref<AiSource | null>(null)

function openAiCurrent(img: ProductImage) {
  aiSource.value = { kind: 'current', imageNo: img.ImageNo, url: img.ImageUrl, ocr: img.ocr, label: `現在 #${img.ImageNo}` }
}
function openAiCand(c: Candidate) {
  aiSource.value = { kind: 'cand', stagingKey: c.stagingKey, url: c.url, ocr: c.ocr, label: candSourceText(c) }
}
function openAiDraft(index: number) {
  const d = index < 0 ? pse.value : draft.value[index]
  if (!d) return
  if (d.ref.kind === 'current') {
    const img = currentByNo.value.get(d.ref.imageNo)
    if (img) openAiCurrent(img)
  } else {
    const c = candById.value.get(d.ref.candId)
    if (c) openAiCand(c)
  }
}
type AiPayload = { items: { job: AiImageEditJob; results: AiImageEditResult[] }[]; sourceLabel: string }
/** AI修正の結果を候補(AI)の先頭に入れる(既にあるもの・削除したものは入れない)。入れた枚数を返す */
function putAiResults(payload: AiPayload): number {
  const known = new Set(candidates.value.map((c) => c.stagingKey))
  const label = payload.sourceLabel.replace(/^現在 /, '')
  // 新しい実行の結果ほど前に並べる
  const added: Candidate[] = [...payload.items].reverse().flatMap((it) =>
    it.results
      .filter((r) => !known.has(r.stagingKey) && !hiddenKeys.has(r.stagingKey))
      .map((r) => aiCandidate(r, it.job.jobId, new Date(), label, it.job.prompt)),
  )
  // 新しい結果はAIの群の先頭に入れる(新しい順)
  const others = candidates.value.filter((c) => c.origin !== 'ai')
  const ai = candidates.value.filter((c) => c.origin === 'ai')
  candidates.value = [...others, ...added, ...ai]
  return added.length
}
/** AI修正画面で結果ができた: その場で候補(AI)に入れる(AI修正画面の後ろで見えている) */
function onAiResults(p: { job: AiImageEditJob; results: AiImageEditResult[]; sourceLabel: string }) {
  putAiResults({ items: [{ job: p.job, results: p.results }], sourceLabel: p.sourceLabel })
}

// ---------------------------------------------------------------------------
// 編集(ペイントツール)。保存した画像は候補に入り、「登録する画像」(17番画像も)にその画像があれば差し替える
// ---------------------------------------------------------------------------
interface PaintTarget {
  url: string
  label: string
  /** 差し替える対象(この参照の画像が登録する画像に入っていれば差し替える) */
  ref: DraftRef
  /** 保存した画像の候補の区分(17番画像の群の画像を編集したら17番画像の群に入れる) */
  origin: CandidateOrigin
}
const paintTarget = ref<PaintTarget | null>(null)

function openPaintCurrent(img: ProductImage) {
  paintTarget.value = { url: img.ImageUrl, label: `現在 #${img.ImageNo}`, ref: { kind: 'current', imageNo: img.ImageNo }, origin: 'local' }
}
function openPaintCand(c: Candidate) {
  paintTarget.value = {
    url: c.url,
    label: candSourceText(c),
    ref: { kind: 'cand', candId: c.id },
    origin: c.origin === 'pse' ? 'pse' : 'local',
  }
}
function openPaintDraft(index: number) {
  const d = index < 0 ? pse.value : draft.value[index]
  if (!d) return
  if (d.ref.kind === 'current') {
    const img = currentByNo.value.get(d.ref.imageNo)
    if (img) openPaintCurrent(img)
  } else {
    const c = candById.value.get(d.ref.candId)
    if (c) openPaintCand(c)
  }
}
function sameRef(a: DraftRef, b: DraftRef) {
  return a.kind === 'current' ? b.kind === 'current' && a.imageNo === b.imageNo : b.kind === 'cand' && a.candId === b.candId
}
function onPaintSaved(p: { stagingKey: string; url: string }) {
  const t = paintTarget.value
  if (!t) return
  objectUrls.push(p.url)
  // 17番画像を編集したときは、保存した画像も候補の「17番画像」の群に入れる
  const isPse = !!pse.value && sameRef(pse.value.ref, t.ref)
  const c: Candidate = {
    id: newId('edit'),
    origin: isPse ? 'pse' : t.origin,
    stagingKey: p.stagingKey,
    url: p.url,
    label: `編集 ${shortDate(new Date())}`,
    sub: `${t.label.replace(/^現在 /, '')}を編集`,
  }
  candidates.value = [...candidates.value, c]
  const newRef: DraftRef = { kind: 'cand', candId: c.id }
  let replaced = false
  draft.value = draft.value.map((d) => {
    if (!sameRef(d.ref, t.ref)) return d
    replaced = true
    return { key: newId('d'), ref: newRef }
  })
  if (pse.value && sameRef(pse.value.ref, t.ref)) {
    pse.value = { key: newId('pse'), ref: newRef }
    replaced = true
  }
  paintTarget.value = null
  toast.show(replaced ? '編集した画像に差し替えました。' : '編集した画像を候補に入れました。')
}

// ---------------------------------------------------------------------------
// 候補の「削除」
//   AIの結果: 非表示にする(POST /images/ai-edits/hide)。S3の画像は残し、画面を開き直しても出さない
//   Amazon・ローカル: この画面の候補から外すだけ(一時置き場のファイルは期限で消える)
// ---------------------------------------------------------------------------
const hiddenKeys = new Set<string>()
async function removeCandidate(c: Candidate) {
  if (isCandUsed(c)) return
  const before = candidates.value
  candidates.value = before.filter((x) => x.id !== c.id)
  if (c.origin !== 'ai' || !c.aiJobId || c.aiIndex === undefined) return
  hiddenKeys.add(c.stagingKey)
  try {
    await api.hideAiImageEdits([{ jobId: c.aiJobId, index: c.aiIndex }])
  } catch (e) {
    hiddenKeys.delete(c.stagingKey)
    // 失敗したら元の位置に戻す
    const idx = before.findIndex((x) => x.id === c.id)
    const now = [...candidates.value]
    now.splice(Math.min(idx, now.length), 0, c)
    candidates.value = now
    toast.show(`削除できませんでした: ${(e as Error).message}`, 'ng')
  }
}

// ---------------------------------------------------------------------------
// 関連ファイル(子ASIN)。商品詳細の「関連ファイル(子ASIN)」と同じ(OpenProduct.bat に子ASIN・セラーID)
// ---------------------------------------------------------------------------
const selection = useSelectionStore()
const { data: fileCounts } = useRelatedFileCounts(
  computed(() => props.parentAsin ?? null),
  asinRef,
  computed(() => selection.selectedSellerId),
)
const childFileCount = computed(() => fileCounts.value?.childAsin ?? null)
function openRelatedFiles() {
  runRelatedFileBat('OpenProduct.bat', props.asin, selection.selectedSellerId ?? '')
}

// ---------------------------------------------------------------------------
// 登録 / キャンセル
// ---------------------------------------------------------------------------
const saveMutation = useSaveProductImagesMutation()
const isBusy = computed(() => saveMutation.isPending.value || uploadingCount.value > 0)

async function save() {
  if (!baseVersion.value) {
    resultModal.show('画像一覧の版が取得できないため登録できません(APIを更新してください)。', 'ng')
    return
  }
  if (!isDirty.value) {
    emit('close')
    return
  }
  if (allSelected.value.length === 0 && !(await confirmModal.ask('画像をすべて外して登録します。よろしいですか？'))) return
  if (ngInDraft.value > 0 && !(await confirmModal.ask(`NGワードを含む画像が${ngInDraft.value}枚あります。このまま登録しますか？`))) return

  const toItem = (d: DraftItem, isPse: boolean): SaveImageItem => {
    if (d.ref.kind === 'current') return { source: 'current', imageNo: d.ref.imageNo, pse: isPse }
    const c = candById.value.get(d.ref.candId)!
    const origin = c.origin === 'pse' ? 'local' : c.origin
    return { source: 'staging', stagingKey: c.stagingKey, origin, aiJobId: c.aiJobId, pse: isPse }
  }
  const images: SaveImageItem[] = draft.value.map((d) => toItem(d, false))
  if (pse.value) images.push(toItem(pse.value, true))
  saveMutation.mutate(
    { asin: props.asin, req: { regionCode: props.regionCode, version: baseVersion.value, images } },
    {
      onSuccess: (r) => {
        resultModal.show(
          r.changed ? `画像を登録しました。(${r.imageCount}枚${pse.value ? '・17番画像あり' : ''})` : '変更はありませんでした。',
        )
        emit('close')
      },
      onError: (err) => {
        const msg = (err as Error).message
        if (err instanceof ApiError && err.status === 409) resultModal.show(msg, 'ng')
        else resultModal.show(`画像の登録に失敗しました: ${msg}`, 'ng')
      },
    },
  )
}

async function cancel() {
  if (saveMutation.isPending.value) return
  if (isDirty.value && ready.value && !(await confirmModal.ask('編集中の内容を破棄して閉じますか？'))) return
  emit('close')
}

function onBeforeUnload(e: BeforeUnloadEvent) {
  if (isDirty.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(() => {
  init()
  window.addEventListener('keydown', onKeydown, true)
  window.addEventListener('beforeunload', onBeforeUnload)
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown, true)
  window.removeEventListener('beforeunload', onBeforeUnload)
  for (const u of objectUrls) URL.revokeObjectURL(u)
})
</script>

<template>
  <div class="ie-overlay">
    <div class="ie-modal" role="dialog" aria-modal="true" aria-label="画像編集">
      <!-- ヘッダー -->
      <div class="ie-head">
        <div class="ie-head-title">
          <h2>画像編集</h2>
          <span class="ie-asin">{{ asin }}</span>
        </div>
        <button class="ie-icon-btn" aria-label="閉じる" title="閉じる" @click="cancel">
          <svg class="icon" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div v-if="loadError" class="ie-body"><div class="empty-state">{{ loadError }}</div></div>
      <div v-else-if="!ready" class="ie-body"><div class="empty-state"><span class="spinner"></span></div></div>

      <div v-else class="ie-body">
        <!-- 1行目: 現在の画像 -->
        <section class="ie-row">
          <div class="ie-row-head">
            <h3>現在の画像</h3>
            <span class="ie-hint">登録済み・{{ currentImages.length }}枚。「追加」またはドラッグで「登録する画像」へ</span>
          </div>
          <div class="ie-strip">
            <div v-if="currentImages.length === 0" class="ie-empty-slot">登録済みの画像はありません</div>
            <div
              v-for="(img, idx) in currentImages"
              :key="img.ImageNo"
              class="ie-tile"
              :class="{ 'ie-used': currentInDraft.has(img.ImageNo) }"
            >
              <div
                class="ie-frame"
                :draggable="!currentInDraft.has(img.ImageNo)"
                title="クリックで拡大"
                @dragstart="onDragStart($event, { type: 'current', imageNo: img.ImageNo })"
                @dragend="onDragEnd"
                @click="openLbCurrent(idx)"
              >
                <img :src="img.ImageUrl" :alt="`現在 ${img.ImageNo}`" loading="lazy" draggable="false" />
                <span v-if="currentInDraft.has(img.ImageNo)" class="ie-selected">選択中</span>
              </div>
              <div class="ie-meta">
                <span class="ie-no" :class="{ 'ie-no-pse': img.ImageNo === 17 }" :title="img.ImageNo === 17 ? '17番画像(PSE画像等)' : ''">{{ img.ImageNo }}</span>
                <span v-for="(c, i) in ocrChips(img.ocr)" :key="i" :class="c.cls" :title="c.title">{{ c.text }}</span>
              </div>
              <div class="ie-actions">
                <button v-if="!currentInDraft.has(img.ImageNo)" class="ie-add-btn" @click="addCurrent(img.ImageNo)">
                  <svg class="icon icon-xs" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>追加
                </button>

                <button class="ie-edit-btn" title="編集（ペイントツール）" aria-label="編集" @click="openPaintCurrent(img)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg></button>
                  <button class="ie-ai-btn" title="AIで修正" aria-label="AIで修正" @click="openAiCurrent(img)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 2行目: 候補 -->
        <section class="ie-row" @dragover.prevent @drop="onCandDrop">
          <div class="ie-row-head">
            <h3>候補</h3>
            <span class="ie-hint">登録するまで反映されません。「追加」で「登録する画像」の末尾へ</span>
            <span class="ie-grow"></span>
            <button class="btn btn-ghost btn-sm" title="関連ファイルのフォルダを開きます(拡張機能が無い場合はパスをコピーします)" @click="openRelatedFiles">
              <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
              関連ファイル(子ASIN)
              <span
                v-if="childFileCount"
                class="file-badge"
                :class="{ zero: childFileCount.fileCount === 0 }"
                :title="`関連ファイル ${childFileCount.fileCount} 件`"
              >{{ childFileCount.fileCount }}</span>
            </button>
            <button class="btn btn-ghost btn-sm" :disabled="amazonState === 'loading'" @click="fetchAmazon">
              <span v-if="amazonState === 'loading'" class="spinner spinner-sm"></span>
              {{ amazonState === 'done' ? 'Amazonから再取得' : 'Amazonから取得' }}
            </button>
            <button class="btn btn-ghost btn-sm" @click="pickFiles">ファイルを追加</button>
            <input
              ref="fileInput"
              type="file"
              multiple
              :accept="ACCEPT_IMAGE_TYPES.join(',')"
              style="display: none"
              @change="onFilesPicked"
            />
            <!-- 17番画像用。v-for の中に置くと ref が配列になりクリックで開けないため、ここに置く -->
            <input
              ref="pseInput"
              type="file"
              :accept="ACCEPT_IMAGE_TYPES.join(',')"
              style="display: none"
              @change="onPseFilePicked"
            />
          </div>
          <div class="ie-strip ie-groups">
            <div v-for="g in groupedCandidates" :key="g.origin" class="ie-group">
              <div class="ie-group-head">
                <span class="ie-src" :class="`ie-src-${g.origin}`">{{ g.label }}</span>
                <span class="ie-count">{{ g.items.length }}</span>
                <span v-if="g.origin === 'amazon' && amazonFetchedAt" class="ie-note">取得 {{ amazonFetchedAt }}</span>
                <span v-if="g.origin === 'pse'" class="ie-note">PSE画像等、必ずモールへ表示する画像</span>
                <span v-if="g.origin === 'ai'" class="ie-note">
                  <template v-if="aiHistoryLoading"><span class="spinner spinner-sm"></span> 過去の結果を読み込み中</template>
                  <template v-else-if="g.items.length > 0">新しい順・過去の結果も含む</template>
                </span>
              </div>
              <div class="ie-group-items">
                <div
                  v-for="c in g.items"
                  :key="c.id"
                  class="ie-tile"
                  :class="{ 'ie-used': isCandUsed(c) }"
                >
                  <div
                    class="ie-frame"
                    :draggable="!isCandUsed(c)"
                    title="クリックで拡大"
                    @dragstart="onDragStart($event, { type: 'cand', id: c.id })"
                    @dragend="onDragEnd"
                    @click="openLbCand(c)"
                  >
                    <img :src="c.url" :alt="c.label" loading="lazy" draggable="false" />
                    <span v-if="isCandUsed(c)" class="ie-selected">選択中</span>
                    <button
                      v-else
                      class="ie-del-btn"
                      :aria-label="`${c.label} を候補から削除`"
                      :title="c.origin === 'ai' ? '候補から削除（画像は保存されたまま。開き直しても出ません）' : '候補から削除'"
                      @click.stop="removeCandidate(c)"
                    >
                      <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13" /><path d="M9 7V4h6v3" /></svg>削除
                    </button>
                  </div>
                  <div class="ie-meta">
                    <span class="ie-tag" :title="c.title ?? c.label">{{ c.label }}</span>
                    <span v-if="c.match?.kind === 'same'" class="ie-chip">現在 #{{ c.match.imageNo }} と同じ</span>
                    <span v-else-if="c.match?.kind === 'near'" class="ie-chip">現在 #{{ c.match.imageNo }} に近い</span>
                    <span v-for="(oc, i) in ocrChips(c.ocr, c.ocrChecking, 'OCR未')" :key="i" :class="oc.cls" :title="oc.title">{{ oc.text }}</span>
                  </div>
                  <div v-if="c.sub" class="ie-sub">{{ c.sub }}</div>
                  <div class="ie-actions">
                    <button v-if="!isCandUsed(c)" class="ie-add-btn" :title="c.origin === 'pse' ? '17番画像にする' : ''" @click="addCandidate(c)">
                      <svg class="icon icon-xs" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>{{ c.origin === 'pse' ? '17番に' : '追加' }}
                    </button>

                    <button class="ie-edit-btn" title="編集（ペイントツール）" aria-label="編集" @click="openPaintCand(c)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg></button>
                  <button class="ie-ai-btn" title="AIで修正" aria-label="AIで修正" @click="openAiCand(c)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg></button>
                  </div>
                </div>

                <!-- 群ごとの空きの案内 -->
                <template v-if="g.origin === 'amazon'">
                  <div v-if="amazonState === 'loading'" class="ie-empty-slot"><span class="spinner spinner-sm"></span> 取得しています…</div>
                  <div v-else-if="amazonState === 'error'" class="ie-empty-slot ie-empty-error">{{ amazonError }}</div>
                  <button v-else-if="g.items.length === 0" class="ie-empty-slot ie-empty-btn" @click="fetchAmazon">
                    Amazonの最新画像を取得して比べる
                  </button>
                </template>
                <div v-else-if="g.origin === 'ai' && g.items.length === 0" class="ie-empty-slot">
                  画像の「AIで修正」で作った結果がここに入ります
                </div>
                <button v-else-if="g.origin === 'local'" class="ie-empty-slot ie-empty-btn ie-local-drop" @click="pickFiles">
                  <span v-if="uploadingCount > 0"><span class="spinner spinner-sm"></span> アップロード中…</span>
                  <span v-else>ファイルを選ぶ<br />またはドロップ</span>
                </button>
                <!-- 17番画像(PSE画像等)。ここで上げた画像は「17番画像」の群に入り、そのまま17番画像になる -->
                <template v-else-if="g.origin === 'pse'">
                  <button
                    class="ie-empty-slot ie-empty-btn ie-pse-drop"
                    :class="{ 'ie-pse-over': pseDropOver }"
                    @click="pickPseFile"
                    @dragover.prevent.stop="pseDropOver = true"
                    @dragleave="pseDropOver = false"
                    @drop="onPseDrop"
                  >
                    <span v-if="uploadingCount > 0"><span class="spinner spinner-sm"></span> アップロード中…</span>
                    <template v-else>
                      <b v-if="pse">差し替え</b>
                      <span>ファイルを選ぶ<br />またはドロップ</span>
                    </template>
                  </button>
                </template>
              </div>
            </div>

          </div>
        </section>

        <!-- 3行目: 登録する画像 -->
        <section class="ie-row ie-row-draft">
          <div class="ie-row-head">
            <h3>登録する画像</h3>
            <span class="ie-hint">この並びで登録（一番左がメイン画像）。ドラッグで並べ替え、「除外」で外す。17番画像は「ここへドラッグ」の右に別枠</span>
            <span class="ie-grow"></span>
            <button
              class="btn btn-ghost btn-sm"
              :disabled="!candidates.some((c) => c.origin === 'amazon')"
              title="Amazonの画像の並びにします。現在の画像と同じものは現在の画像を使います"
              @click="replaceWithAmazon"
            >Amazonの画像で置き換え</button>
            <button class="btn btn-ghost btn-sm" :disabled="!isDirty" @click="resetToCurrent">現在に戻す</button>
          </div>
          <div
            class="ie-strip ie-draft-strip"
            @dragover="onDraftAreaDragOver"
            @dragleave="onDraftAreaDragLeave"
            @drop="onDraftDrop"
          >
            <template v-for="(d, idx) in draftView" :key="d.key">
              <div v-if="dropIndex === idx" class="ie-drop-bar"></div>
              <div
                class="ie-tile"
                :class="{ 'ie-dragging': dragging?.type === 'draft' && dragging.index === idx, 'ie-tile-main': idx === 0 }"
                @dragover="onDraftTileDragOver($event, idx)"
              >
                <div class="ie-tile-label" :class="{ 'ie-tile-label-main': idx === 0 }">{{ idx === 0 ? 'メイン画像' : '' }}</div>
                <div
                  class="ie-frame"
                  draggable="true"
                  title="ドラッグで並べ替え・クリックで拡大"
                  @dragstart="onDragStart($event, { type: 'draft', index: idx })"
                  @dragend="onDragEnd"
                  @click="openLbDraft(idx)"
                >
                  <img :src="d.url" :alt="d.srcText" loading="lazy" draggable="false" />
                </div>
                <div class="ie-meta">
                  <span class="ie-src" :class="`ie-src-${d.kind}`" :title="d.srcText">{{ d.srcText }}</span>
                  <span v-if="d.isNew" class="ie-chip ie-chip-new">新規</span>
                  <span v-for="(oc, i) in draftChips(d)" :key="i" :class="oc.cls" :title="oc.title">{{ oc.text }}</span>
                </div>
                <div class="ie-actions">
                  <button class="ie-exclude-btn" title="登録する画像から外す" @click="removeDraft(idx)">除外</button>
                  <button class="ie-edit-btn" title="編集（ペイントツール）" aria-label="編集" @click="openPaintDraft(idx)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg></button>
                  <button class="ie-ai-btn" title="AIで修正" aria-label="AIで修正" @click="openAiDraft(idx)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg></button>
                </div>
              </div>
            </template>
            <div v-if="dropIndex === draftView.length" class="ie-drop-bar"></div>
            <div class="ie-empty-slot ie-drop-slot ie-below-label">ここへドラッグ<br />ファイルもドロップ可</div>

            <!-- 17番画像(並びとは別。常に ImageNo 17)。「ここへドラッグ」の右に置く -->
            <div class="ie-pse-area" @dragover.stop.prevent="pseDropOver = true" @dragleave="pseDropOver = false" @drop="onPseDrop">
              <div v-if="pseView" class="ie-tile">
                <div class="ie-tile-label ie-tile-label-pse">17番画像</div>
                <div
                  class="ie-frame ie-frame-pse"
                  draggable="true"
                  title="17番画像。ドラッグで通常の並びへ移せます・クリックで拡大"
                  @dragstart="onDragStart($event, { type: 'pse' })"
                  @dragend="onDragEnd"
                  @click="openLbDraft(draftView.length)"
                >
                  <img :src="pseView.url" :alt="pseView.srcText" loading="lazy" draggable="false" />
                </div>
                <div class="ie-meta">
                  <span class="ie-src" :class="`ie-src-${pseView.kind}`" :title="pseView.srcText">{{ pseView.srcText }}</span>
                  <span v-if="pseView.isNew" class="ie-chip ie-chip-new">新規</span>
                  <span v-for="(oc, i) in draftChips(pseView)" :key="i" :class="oc.cls" :title="oc.title">{{ oc.text }}</span>
                </div>
                <div class="ie-actions">
                  <button class="ie-exclude-btn" title="17番画像を外す(17は使わない)" @click="removePse">除外</button>
                  <button class="ie-edit-btn" title="編集（ペイントツール）" aria-label="編集" @click="openPaintDraft(-1)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg></button>
                  <button class="ie-ai-btn" title="AIで修正" aria-label="AIで修正" @click="openAiDraft(-1)"><svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg></button>
                </div>
              </div>
              <div v-if="!pseView" class="ie-tile-label ie-tile-label-pse">17番画像</div>
              <div v-if="!pseView" class="ie-empty-slot ie-pse-empty" :class="{ 'ie-pse-over': pseDropOver }">
                <b>17番画像なし</b>
                <span>候補の「17番画像」で指定<br />（ここへドラッグも可）</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- フッター -->
      <div class="ie-foot">
        <div class="ie-foot-summary">
          <template v-if="ready && isDirty">
            <span class="ie-dot"></span>
            <b>未登録の変更があります</b>
            <span>
              追加 <b>{{ summary.added }}</b> ・ 外す <b>{{ summary.removed }}</b>
              <template v-if="summary.reordered"> ・ 並び替えあり</template>
              <template v-if="needsRenumber"> ・ 番号を0から振り直し</template>
            </span>
            <span v-if="ngInDraft > 0" class="ie-foot-warn">NGワードを含む画像 {{ ngInDraft }}枚</span>
          </template>
          <span class="ie-hint">登録するまで商品画像は変わりません</span>
        </div>
        <div class="ie-foot-actions">
          <button class="btn btn-ghost" :disabled="saveMutation.isPending.value" @click="cancel">キャンセル</button>
          <button class="btn btn-primary" :disabled="!ready || isBusy" @click="save">
            <span v-if="saveMutation.isPending.value" class="spinner spinner-sm"></span>
            {{ saveMutation.isPending.value ? '登録中…' : '登録する' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 拡大表示 -->
    <div v-if="lb && lbItem" class="lightbox-overlay ie-lightbox" @click="lb = null">
      <div class="lightbox-body" @click.stop>
        <div class="lightbox-stage" @click.self="lb = null">
          <button class="lightbox-close" title="閉じる (Esc)" @click.stop="lb = null">
            <svg class="icon" viewBox="0 0 24 24" style="color: #fff; width: 20px; height: 20px"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <button v-if="lb.items.length > 1" class="lightbox-nav lightbox-nav-prev" title="前の画像 (←)" @click.stop="lbStep(-1)">
            <span class="lightbox-nav-circle"><svg class="icon" viewBox="0 0 24 24" style="width: 22px; height: 22px"><path d="M15 18l-6-6 6-6" /></svg></span>
          </button>
          <span class="img-no-badge img-no-badge-lg lightbox-no-badge">{{ lbItem.label }}</span>
          <OcrImage
            :src="lbItem.url"
            :ocr="lbItem.ocr"
            :active-index="lbActiveHit"
            :show-all="lbShowBoxes"
            img-class="lightbox-image"
          />
          <button v-if="lb.items.length > 1" class="lightbox-nav lightbox-nav-next" title="次の画像 (→)" @click.stop="lbStep(1)">
            <span class="lightbox-nav-circle"><svg class="icon" viewBox="0 0 24 24" style="width: 22px; height: 22px"><path d="M9 6l6 6-6 6" /></svg></span>
          </button>
          <div v-if="lb.items.length > 1" class="lightbox-counter">{{ lb.index + 1 }} / {{ lb.items.length }}</div>
        </div>
        <OcrNgList
          v-if="lbHits.length > 0"
          v-model:show-boxes="lbShowBoxes"
          :hits="lbHits"
          :active-index="lbActiveHit"
          @hover="lbActiveHit = $event"
        />
      </div>
    </div>

    <PaintEditModal
      v-if="paintTarget"
      :url="paintTarget.url"
      :label="paintTarget.label"
      @close="paintTarget = null"
      @saved="onPaintSaved"
    />

    <AiEditModal
      v-if="aiSource"
      :asin="asin"
      :region-code="regionCode"
      :source="aiSource"
      @close="aiSource = null"
      @results="onAiResults"
    />
  </div>
</template>

<style scoped>
.ie-overlay{
  position:fixed;inset:0;z-index:1050;background:rgba(10,14,20,.6);
  display:flex;align-items:stretch;justify-content:center;padding:8px;
  animation:fadeIn .12s ease;
}
.ie-modal{
  width:100%;background:var(--panel-alt);border:1px solid var(--line);border-radius:12px;
  box-shadow:var(--shadow-md);display:flex;flex-direction:column;overflow:hidden;min-height:0;
}
.ie-head{
  display:flex;align-items:center;gap:12px;padding:6px 14px;background:var(--panel);border-bottom:1px solid var(--line);
}
.ie-head-title{display:flex;align-items:baseline;gap:12px;flex:1;min-width:0;}
.ie-head-title h2{margin:0;font-size:16px;font-weight:700;color:var(--brand-dark);}
.ie-asin{font-family:var(--font-mono);font-size:13px;background:var(--mono-bg);border:1px solid var(--mono-line);border-radius:6px;padding:1px 8px;}
.ie-icon-btn{
  width:30px;height:30px;border-radius:8px;border:1px solid var(--line);background:var(--panel);
  display:inline-flex;align-items:center;justify-content:center;color:var(--ink-soft);
}
.ie-icon-btn:hover{background:var(--panel-alt);color:var(--ink);}
.ie-body{flex:1;overflow-y:auto;padding:8px 12px;display:flex;flex-direction:column;gap:8px;min-height:0;}

.ie-row{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:7px 12px 8px;}
.ie-row-draft{border:2px solid var(--brand);}
.ie-row-draft h3{color:var(--brand);}
.ie-row-head{display:flex;align-items:center;flex-wrap:wrap;gap:4px 12px;margin-bottom:6px;min-height:26px;}
.ie-row-head h3{margin:0;font-size:14px;font-weight:700;}
.ie-hint{font-size:11.5px;color:var(--ink-faint);}
.ie-grow{flex:1 1 auto;}

.ie-strip{display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;align-items:flex-start;min-height:60px;}
.ie-groups{gap:0;align-items:stretch;}
.ie-group{flex:none;display:flex;flex-direction:column;gap:4px;padding:0 14px;border-left:1px solid var(--line-soft);}
.ie-group:first-child{padding-left:0;border-left:none;}
.ie-group-head{display:flex;align-items:center;gap:8px;height:20px;}
.ie-count{font-family:var(--font-mono);font-size:12px;color:var(--ink-soft);}
.ie-note{font-size:11px;color:var(--ink-faint);white-space:nowrap;}
.ie-group-items{display:flex;gap:12px;align-items:flex-start;}

.ie-tile{flex:none;width:150px;display:flex;flex-direction:column;gap:4px;transition:opacity .12s, filter .12s;}
/* メイン画像(一番左)は、2枚目以降と点線で区切る */
.ie-tile-main{width:150px;padding-right:12px;margin-right:2px;border-right:2px dashed var(--brand-tint-line);box-sizing:content-box;}
.ie-used .ie-frame img{opacity:.35;filter:grayscale(1);}
.ie-selected{
  position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;
  font-size:15px;font-weight:700;letter-spacing:.15em;color:rgba(31,36,48,.55);background:rgba(246,246,242,.35);
}
.ie-dragging{opacity:.35;}
.ie-frame{
  position:relative;width:150px;height:150px;border:1px solid var(--line);border-radius:6px;overflow:hidden;
  background:#fff;cursor:grab;
}
.ie-used .ie-frame{cursor:zoom-in;}
.ie-frame img{width:100%;height:100%;object-fit:contain;display:block;pointer-events:none;}
.ie-del-btn{
  position:absolute;top:6px;right:6px;display:inline-flex;align-items:center;gap:3px;padding:3px 7px;font-size:11px;font-weight:600;
  color:var(--ng);background:rgba(255,255,255,.92);border:1px solid var(--line);border-radius:6px;cursor:pointer;
}
.ie-del-btn:hover{border-color:currentColor;background:var(--ng-tint);}
.ie-frame-no{
  position:absolute;top:6px;left:6px;font-family:var(--font-mono);font-size:12px;color:#fff;background:var(--ink-soft);
  border-radius:4px;padding:0 6px;line-height:18px;
}
.ie-remove{
  position:absolute;top:5px;right:5px;width:24px;height:24px;border:none;border-radius:12px;
  background:rgba(31,36,48,.78);color:#fff;display:inline-flex;align-items:center;justify-content:center;padding:0;
}
.ie-remove .icon{width:12px;height:12px;stroke-width:2.6;}
.ie-remove:hover{background:var(--ng);}
.ie-meta{display:flex;align-items:center;gap:5px;flex-wrap:wrap;min-height:18px;}
.ie-sub{font-size:11px;color:var(--ink-faint);min-height:14px;line-height:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ie-no{
  font-family:var(--font-mono);font-size:12px;color:#fff;background:var(--ink-soft);border-radius:4px;padding:0 6px;line-height:18px;
}
.ie-tag{
  font-family:var(--font-mono);font-size:11px;background:var(--mono-bg);border:1px solid var(--mono-line);border-radius:4px;
  padding:0 5px;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
}
.ie-chip{font-size:11px;color:var(--ink-faint);background:var(--prep-tint);border:1px solid var(--line);border-radius:4px;padding:0 5px;}
.ie-chip-new{color:var(--ok);background:var(--ok-tint);border-color:var(--ok-line);font-weight:700;}
.ie-src{font-size:11px;border-radius:4px;padding:0 6px;border:1px solid;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;font-weight:600;}
.ie-src-current{color:var(--ink-soft);background:var(--mono-bg);border-color:var(--mono-line);font-weight:400;}
.ie-src-amazon{color:var(--brand);background:var(--brand-tint);border-color:var(--brand-tint-line);}
.ie-src-ai{color:#5B3E96;background:#EFEAF8;border-color:#D4C8EC;}
.ie-src-local{color:#2F5D50;background:#E6F0EC;border-color:#BFD8CE;}

.ie-actions{display:flex;align-items:center;gap:4px;min-height:24px;}
.ie-spacer{flex:1 1 auto;}
.ie-add-btn{
  flex:1 1 auto;height:26px;padding:0 6px;border:1px solid var(--brand-dark);background:var(--brand);color:#fff;
  border-radius:5px;font-size:12px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;gap:3px;
}
.ie-add-btn:hover{background:var(--brand-dark);}
.ie-add-btn .icon{stroke-width:3;}
.ie-ai-btn{
  flex:none;width:30px;height:26px;padding:0;border:1px solid #D4C8EC;background:#EFEAF8;color:#5B3E96;border-radius:5px;
  font-size:12px;font-weight:600;display:inline-flex;align-items:center;justify-content:center;gap:3px;white-space:nowrap;
}
.ie-ai-btn:hover{background:#E3DAF4;}
.ie-ai-btn .icon{width:15px;height:15px;}
.ie-edit-btn{
  flex:none;width:30px;height:26px;padding:0;border:1px solid var(--line);background:var(--panel);color:var(--ink-soft);border-radius:5px;
  display:inline-flex;align-items:center;justify-content:center;
}
.ie-edit-btn:hover{background:var(--brand-tint);color:var(--brand);border-color:var(--brand-tint-line);}
.ie-edit-btn .icon{width:14px;height:14px;}
.ie-exclude-btn{
  flex:1 1 auto;height:26px;padding:0 6px;border:1px solid var(--ng-line);background:var(--panel);color:var(--ng);border-radius:5px;
  font-size:12px;font-weight:600;
}
.ie-exclude-btn:hover{background:var(--ng-tint);}
.ie-mini-btn{
  width:26px;height:26px;flex:none;border:1px solid var(--line);background:var(--panel);border-radius:5px;color:var(--ink-soft);
  display:inline-flex;align-items:center;justify-content:center;padding:0;
}
.ie-mini-btn:hover{background:var(--brand-tint);color:var(--brand);}
.ie-mini-btn.ie-ai{color:#5B3E96;background:#EFEAF8;border-color:#D4C8EC;}
.ie-mini-btn.ie-ai:hover{background:#E3DAF4;}

.ie-empty-slot{
  flex:none;width:150px;min-height:150px;box-sizing:border-box;border:2px dashed var(--line);border-radius:6px;
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;text-align:center;
  padding:10px;font-size:12px;color:var(--ink-faint);background:var(--panel-alt);line-height:1.5;
}
.ie-empty-btn{cursor:pointer;font-family:inherit;}
.ie-empty-btn:hover{border-color:var(--brand-tint-line);color:var(--brand);background:var(--brand-tint);}
.ie-empty-error{color:var(--ng);border-color:var(--ng-line);background:var(--ng-tint);}
.ie-local-drop{border-color:#BFD8CE;background:#F3F8F6;color:#2F5D50;}
.ie-drop-slot{border-color:var(--brand-tint-line);background:var(--brand-tint);color:var(--brand);font-weight:500;}
.ie-src-pse{color:#8A4B00;background:#FFF1DC;border-color:#F0C98A;}
.ie-no-pse{background:#B86E00;}
.ie-tile-label{height:16px;line-height:16px;font-size:11.5px;font-weight:700;letter-spacing:.04em;white-space:nowrap;}
.ie-tile-label-main{color:var(--brand);}
.ie-tile-label-pse{color:#B86E00;}
.ie-below-label{margin-top:20px;}
.ie-frame-pse{border:2px solid #E3A64A;}
.ie-pse-drop{border-color:#F0C98A;background:#FFF8EE;color:#8A4B00;}
.ie-pse-drop:hover,.ie-pse-over{border-color:#B86E00 !important;background:#FFF1DC !important;color:#8A4B00 !important;}
.ie-pse-area{flex:none;display:flex;flex-direction:column;gap:4px;padding-left:14px;border-left:2px dashed #F0C98A;}
.ie-pse-empty{border-color:#F0C98A;background:#FFFBF5;color:#A07040;}
.ie-pse-empty b{color:#8A4B00;}
.ie-drop-bar{flex:none;width:4px;align-self:stretch;min-height:150px;margin-top:20px !important;border-radius:2px;background:var(--brand);margin:0 -8px;}

.ie-foot{
  display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px;
  padding:6px 14px;background:var(--panel);border-top:1px solid var(--line);
}
.ie-foot-summary{display:flex;align-items:center;flex-wrap:wrap;gap:6px 12px;font-size:13px;}
.ie-dot{width:9px;height:9px;border-radius:5px;background:var(--hold);}
.ie-foot-warn{font-size:12px;font-weight:700;color:var(--ng);background:var(--ng-tint);border:1px solid var(--ng-line);border-radius:4px;padding:0 6px;}
.ie-foot-actions{display:flex;gap:10px;}
.ie-foot-actions .btn{min-width:110px;padding:8px 18px;font-size:13px;}

.ie-lightbox{z-index:1070;}
</style>
